import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUser } from '@clerk/clerk-react';
import { useRespondToMessage } from '../../../hooks/queries/useClientMessages';
import { X, Upload, Paperclip, AlertCircle, CheckCircle } from 'lucide-react';

/**
 * Message Response Modal
 * Allows clients to respond to attorney messages with text and file uploads.
 */
function MessageResponseModal({ isOpen, onClose, message }) {
  const { t } = useTranslation('dashboard');
  const { user } = useUser();
  const respondMutation = useRespondToMessage();

  const [formData, setFormData] = useState({
    message: '',
    items: [],
    attachments: []
  });
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // Initialize items from message request data
  useEffect(() => {
    if (message?.requestData?.items) {
      setFormData(prev => ({
        ...prev,
        items: message.requestData.items.map(item => ({
          ...item,
          response: item.response || ''
        }))
      }));
    }
  }, [message]);

  const handleItemChange = (index, value) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.map((item, i) =>
        i === index ? { ...item, response: value } : item
      )
    }));
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploadingFiles(true);

    try {
      // In a real implementation, this would upload to blob storage
      // For now, we'll store file metadata
      const newAttachments = files.map(file => ({
        name: file.name,
        size: file.size,
        type: file.type,
        // In production, this would be the blob URL after upload
        blobUrl: URL.createObjectURL(file)
      }));

      setFormData(prev => ({
        ...prev,
        attachments: [...prev.attachments, ...newAttachments]
      }));
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Error uploading files. Please try again.');
    } finally {
      setUploadingFiles(false);
    }
  };

  const removeAttachment = (index) => {
    setFormData(prev => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate required items
    const hasRequiredMissing = formData.items.some(
      item => item.required && !item.response?.trim()
    );

    if (hasRequiredMissing) {
      alert(t('messages.requiredFieldsMissing', 'Please fill in all required fields.'));
      return;
    }

    try {
      await respondMutation.mutateAsync({
        messageId: message.id,
        responseData: {
          items: formData.items,
          attachments: formData.attachments,
          message: formData.message,
          clientName: user?.fullName || user?.firstName || 'Client'
        }
      });

      onClose();
    } catch (error) {
      console.error('Error submitting response:', error);
      alert(`Error submitting response: ${error.message}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div
        className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('messages.respondTo', 'Respond to Message')}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              {message.title}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-6">
            {/* Original Message */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                {t('messages.originalMessage', 'Original Message')}
              </h4>
              <p className="text-gray-600 text-sm whitespace-pre-wrap">
                {message.message}
              </p>
            </div>

            {/* Requested Items */}
            {formData.items.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-3">
                  {t('messages.provideInformation', 'Please provide the following information')}
                </h4>
                <div className="space-y-4">
                  {formData.items.map((item, index) => (
                    <div key={index} className="bg-white border border-gray-200 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {item.label}
                        {item.required && <span className="text-red-500 ml-1">*</span>}
                      </label>

                      {item.type === 'text' && (
                        <input
                          type="text"
                          value={item.response || ''}
                          onChange={(e) => handleItemChange(index, e.target.value)}
                          placeholder={t('messages.enterResponse', 'Enter your response...')}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          required={item.required}
                        />
                      )}

                      {item.type === 'date' && (
                        <input
                          type="date"
                          value={item.response || ''}
                          onChange={(e) => handleItemChange(index, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          required={item.required}
                        />
                      )}

                      {item.type === 'file_upload' && (
                        <div>
                          <input
                            type="file"
                            onChange={handleFileUpload}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            {t('messages.fileUploadHint', 'Upload the requested document')}
                          </p>
                        </div>
                      )}

                      {item.type === 'select' && item.options && (
                        <select
                          value={item.response || ''}
                          onChange={(e) => handleItemChange(index, e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          required={item.required}
                        >
                          <option value="">{t('messages.selectOption', 'Select an option...')}</option>
                          {item.options.map((opt, i) => (
                            <option key={i} value={opt}>{opt}</option>
                          ))}
                        </select>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Additional Message */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('messages.additionalComments', 'Additional Comments')}
                <span className="text-gray-500 font-normal ml-1">({t('common.optional', 'optional')})</span>
              </label>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                placeholder={t('messages.additionalCommentsPlaceholder', 'Any additional information you would like to share...')}
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
              />
            </div>

            {/* File Attachments */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('messages.attachFiles', 'Attach Files')}
                <span className="text-gray-500 font-normal ml-1">({t('common.optional', 'optional')})</span>
              </label>

              {/* Uploaded Files */}
              {formData.attachments.length > 0 && (
                <ul className="mb-3 space-y-2">
                  {formData.attachments.map((file, index) => (
                    <li
                      key={index}
                      className="flex items-center justify-between p-2 bg-gray-50 rounded-lg"
                    >
                      <div className="flex items-center gap-2">
                        <Paperclip className="w-4 h-4 text-gray-400" />
                        <span className="text-sm text-gray-700">{file.name}</span>
                      </div>
                      <button
                        type="button"
                        onClick={() => removeAttachment(index)}
                        className="text-red-500 hover:text-red-700"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Upload Button */}
              <label className="flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-teal-500 hover:bg-teal-50/50 transition-colors">
                <Upload className="w-5 h-5 text-gray-400" />
                <span className="text-sm text-gray-600">
                  {uploadingFiles
                    ? t('messages.uploading', 'Uploading...')
                    : t('messages.clickToUpload', 'Click to upload files')}
                </span>
                <input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  disabled={uploadingFiles}
                />
              </label>
            </div>
          </div>

          {/* Footer */}
          <div className="flex items-center justify-end gap-3 p-6 border-t border-gray-200 bg-gray-50">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('common.cancel', 'Cancel')}
            </button>
            <button
              type="submit"
              disabled={respondMutation.isPending}
              className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
            >
              {respondMutation.isPending ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  {t('messages.sending', 'Sending...')}
                </>
              ) : (
                <>
                  <CheckCircle className="w-4 h-4" />
                  {t('messages.submitResponse', 'Submit Response')}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default MessageResponseModal;
